import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { FamilyPaywallModal } from 'us-react-components/dist/FamilyPaywallModal/FamilyPaywallModal';
import { ParentPlanService, Plan, Sitter, SitterService } from 'us-web-services';

import { useDependencies } from '../../dependencies';
import { ConfigService } from '../../services/ConfigService/ConfigService';

const config = new ConfigService();

export const BuyMembership = observer((): JSX.Element => {
  const navigate = useNavigate();
  const { stores, services: { tracker } } = useDependencies();
  const user = stores.user.user;

  const [searchParams] = useSearchParams();
  const planIdParam = searchParams.get('planId');
  const sitterIdParam = searchParams.get('sitterId');
  const planId = planIdParam ? Number(planIdParam) : undefined;
  const sitterId = sitterIdParam ? Number(sitterIdParam) : undefined;

  const [sitter, setSitter] = useState<Sitter | undefined>(undefined);
  const [showModal, setShowModal] = useState(true);
  const [isFirstSubscription, setIsFirstSubscription] = useState(false);

  useEffect(() => {
    document.title = 'UrbanSitter - Buy Membership';
  }, []);

  useEffect(() => {
    if (!sitterId || !user?.id) return;
    const load = async (): Promise<void> => {
      try {
        const resp = await SitterService.get(sitterId, { params: { userId: user.id, parentId: user.id } });
        setSitter(resp.data?.data);
      } catch (err) {
        console.warn('[BuyMembership] sitter load failed', err);
      }
    };
    void load();
  }, [sitterId, user?.id]);

  // Mirrors Angular paywall-service.js:118-126 checkSubscriptions().
  // Required so the conversion event fires `parent-first-plan-added` only
  // for new subscribers — marketing attribution depends on this distinction.
  useEffect(() => {
    if (!user?.id) return;
    const load = async (): Promise<void> => {
      try {
        const resp = await ParentPlanService.getByFilter({
          params: { userId: user.id, parentId: user.id, limit: 1 },
        });
        setIsFirstSubscription((resp.data?.data ?? []).length === 0);
      } catch {
        /* ignore */
      }
    };
    void load();
  }, [user?.id]);

  const handleClose = (): void => {
    setShowModal(false);
    navigate('/parent/packages');
  };

  // Mirrors Angular paywall-service.js:186-205 paywallSuccess(). Pushes the
  // conversion event(s) to GTM dataLayer so Facebook/Google Ads/LinkedIn
  // pixels fire — without this, paid-acquisition attribution is blind.
  const handleSuccess = (plan?: Plan): void => {
    if (plan && user?.id) {
      tracker.triggerGTMEvent({
        event: 'parent-paywall-success',
        conversionValue: plan.fee,
        plan: plan.name,
        uid: user.id,
        paywallTrigger: 'family-paywall',
      });
      if (isFirstSubscription) {
        tracker.triggerGTMEvent({
          event: 'parent-first-plan-added',
          conversionValue: plan.fee,
          plan: plan.name,
          uid: user.id,
          paywallTrigger: 'family-paywall',
        });
        setIsFirstSubscription(false);
      }
    }
    setShowModal(false);
    navigate('/parent/packages');
  };

  if (!user?.id) {
    return <div className='container py-8' />;
  }

  return (
    <FamilyPaywallModal
      showModal={showModal}
      onClose={handleClose}
      onSuccess={handleSuccess}
      planId={planId}
      sitter={sitter}
      userId={user.id}
      stripeKey={config.STRIPE_KEY}
      recaptchaKey={config.RECAPTCHA_KEY}
    />
  );
});
